import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, withKeys as _withKeys, createElementBlock as _createElementBlock, withCtx as _withCtx, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "date-inputs-container flex-center" }
const _hoisted_2 = { class: "input-wrapper" }
const _hoisted_3 = { class: "d-flex align-center mb-2" }
const _hoisted_4 = { class: "text-body-2" }
const _hoisted_5 = { class: "input-wrapper" }
const _hoisted_6 = { class: "d-flex align-center mb-2" }
const _hoisted_7 = { class: "text-body-2" }
const _hoisted_8 = {
  key: 0,
  class: "text-caption text-right text-medium-emphasis position-absolute right-0 mt-1"
}

import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTheme } from 'vuetify'
import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import VueDatePicker from '@vuepic/vue-datepicker'
import { RangeConfig, DatePickerMarker } from '@vuepic/vue-datepicker'
import { dateDifference, getLocalizedDate } from '@/app/system/helper'
import { useDisplay } from 'vuetify'
import '@vuepic/vue-datepicker/dist/main.css'


export default /*@__PURE__*/_defineComponent({
  __name: 'DateRangePicker',
  props: {
    labels: { type: Boolean },
    range: {},
    inline: { type: Boolean, default: false },
    startDate: {},
    endDate: {},
    disabledWeekDays: {}
  },
  emits: ["input"],
  setup(__props: any, { emit: __emit }) {

dayjs.extend(isSameOrBefore)

const props = __props

const defaultRange = computed(() => ({
  minRange: 1,
  partialRange: true
}))

const effectiveRange = computed(() => ({
  ...defaultRange.value,
  ...(props.range || {})
}))

const { locale, t } = useI18n()
const theme = useTheme()
const { mdAndUp, lgAndUp } = useDisplay()

const date = ref<string[]>([
  props.startDate || dayjs().format('YYYY-MM-DD'),
  props.endDate || dayjs().add(1, 'day').format('YYYY-MM-DD')
])
const isSelectingRange = ref(false)
const rangeStartDate = ref<Date | null>(null)

const numNights = computed(() =>
  dateDifference(date.value?.[0], date.value?.[1])
)
const originalDisabledWeekDays = ref(props.disabledWeekDays || [])

const effectiveDisabledWeekDays = computed(() => {
  return isSelectingRange.value ? [] : originalDisabledWeekDays.value
})

const disabledDates = computed(() => {
  return (date: Date) => {
    const today = dayjs().startOf('day')
    if (isSelectingRange.value && rangeStartDate.value) {
      const startDate = dayjs(rangeStartDate.value)
      return dayjs(date).isBefore(startDate, 'day')
    }
    return dayjs(date).isBefore(today, 'day')
  }
})

const createMinStayMarker = (
  date: dayjs.Dayjs,
  minStay: number
): DatePickerMarker => ({
  date: date.toDate(),
  type: 'dot',
  color: '#ffffff00',
  tooltip: [
    {
      text: t('DateRangePicker#Marker#MinimumStay', { minStay }),
      color: '#ffffff00',
      icon: 'calendar-week'
    }
  ]
})

const createMaxStayMarker = (
  date: dayjs.Dayjs,
  maxStay: number
): DatePickerMarker => ({
  date: date.toDate(),
  type: 'dot',
  color: '#ffffff00',
  tooltip: [
    {
      text: t('DateRangePicker#Marker#MaximumStay', { maxStay }),
      color: '#ffffff00',
      icon: 'calendar-xmark'
    }
  ]
})

const getMinStayMarkers = (
  startDate: dayjs.Dayjs,
  minRange: number
): DatePickerMarker[] => {
  const markers: DatePickerMarker[] = []
  const minOffsets = [...Array(minRange - 1).keys()].map((i) => i + 1)

  // Forward markers
  minOffsets.forEach((offset) => {
    markers.push(createMinStayMarker(startDate.add(offset, 'day'), minRange))
  })

  // Backward markers
  minOffsets.forEach((offset) => {
    const beforeDate = startDate.subtract(offset, 'day')
    if (beforeDate.isSameOrAfter(dayjs(), 'day')) {
      markers.push(createMinStayMarker(beforeDate, minRange))
    }
  })

  return markers
}

const getMaxStayMarkers = (
  startDate: dayjs.Dayjs,
  maxRange: number
): DatePickerMarker[] => {
  const markers: DatePickerMarker[] = []

  // Future dates till end of month
  let futureDate = startDate.add(1, 'day')
  const endOfMonth = startDate.endOf('month')
  while (futureDate.isSameOrBefore(endOfMonth)) {
    if (futureDate.diff(startDate, 'day') > maxRange) {
      markers.push(createMaxStayMarker(futureDate, maxRange))
    }
    futureDate = futureDate.add(1, 'day')
  }

  // Past dates from start of month
  let pastDate = startDate.subtract(1, 'day')
  const startOfMonth = startDate.startOf('month')
  while (pastDate.isSameOrAfter(startOfMonth)) {
    if (Math.abs(pastDate.diff(startDate, 'day')) > maxRange) {
      markers.push(createMaxStayMarker(pastDate, maxRange))
    }
    pastDate = pastDate.subtract(1, 'day')
  }

  return markers
}

const dateMarkers = computed(() => {
  if (
    !isSelectingRange.value ||
    !rangeStartDate.value ||
    (!props.range?.minRange && !props.range?.maxRange)
  ) {
    return []
  }

  const startDate = dayjs(rangeStartDate.value)
  const markers: DatePickerMarker[] = []

  if (props.range?.minRange) {
    markers.push(...getMinStayMarkers(startDate, Number(props.range.minRange)))
  }

  if (props.range?.maxRange) {
    markers.push(...getMaxStayMarkers(startDate, Number(props.range.maxRange)))
  }

  return markers
})

watch(
  () => [props.startDate, props.endDate],
  () => {
    const newStartDate = props.startDate || date.value[0]
    const newEndDate = props.endDate || date.value[1]

    if (newStartDate !== date.value[0] || newEndDate !== date.value[1]) {
      date.value = [newStartDate, newEndDate]
    }
  },
  { deep: true }
)

watch(
  () => props.disabledWeekDays,
  (newValue) => {
    originalDisabledWeekDays.value = newValue || []
  }
)

watch(
  date,
  (newDates) => {
    if (newDates && newDates.length === 2) {
      emit('input', {
        start: dayjs(newDates[0]).format('YYYY-MM-DD'),
        end: dayjs(newDates[1]).format('YYYY-MM-DD')
      })
    }
  },
  { deep: true }
)

const formatDateValue = (value: string, index: number): string => {
  if (!value) return ''
  const dates = value.split(' - ')
  const date = dates[index]
  if (!date || date.trim() === '') return ''

  return getLocalizedDate(date, locale.value) || ''
}

const handleRangeStart = (value: Date) => {
  console.log('handleRangeStart', {
    date: dayjs(value).format('YYYY-MM-DD'),
    minRange: props.range?.minRange,
    maxRange: props.range?.maxRange
  })
  isSelectingRange.value = true
  rangeStartDate.value = value
}

const handleRangeEnd = () => {
  console.log('handleRangeEnd')
  isSelectingRange.value = false
  rangeStartDate.value = null
}

const handleClose = () => {
  console.log('handleClose')
  isSelectingRange.value = false
  rangeStartDate.value = null
}

const emit = __emit

return (_ctx: any,_cache: any) => {
  const _component_fa_icon = _resolveComponent("fa-icon")!
  const _component_v_text_field = _resolveComponent("v-text-field")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(['date-range-inputs', { 'w-100': _unref(lgAndUp) }])
  }, [
    _createVNode(_unref(VueDatePicker), {
      modelValue: date.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((date).value = $event)),
      ref: "datepicker",
      dark: _unref(theme).global.current.value.dark ? true : false,
      locale: _unref(locale),
      inline: _ctx.inline,
      clearable: false,
      "enable-time-picker": false,
      "auto-apply": "",
      "multi-calendars": _unref(mdAndUp),
      "min-date": _unref(dayjs)().startOf('day').toDate(),
      position: "left",
      range: effectiveRange.value,
      markers: dateMarkers.value,
      "disabled-dates": disabledDates.value,
      "disabled-week-days": effectiveDisabledWeekDays.value,
      onRangeStart: handleRangeStart,
      onRangeEnd: handleRangeEnd,
      onClosed: handleClose
    }, {
      "marker-tooltip": _withCtx(({ tooltip }) => [
        (tooltip.icon)
          ? (_openBlock(), _createBlock(_component_fa_icon, {
              key: 0,
              class: "mr-2",
              icon: ['far', tooltip.icon]
            }, null, 8, ["icon"]))
          : _createCommentVNode("", true),
        _createElementVNode("div", null, _toDisplayString(tooltip.text), 1)
      ]),
      "dp-input": _withCtx(({ value, onInput, onEnter, onBlur }) => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createVNode(_component_fa_icon, {
                class: "mr-2",
                icon: ['far', 'calendar']
              }),
              _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(t)('Search#Form#Field Arrival')), 1)
            ]),
            _createVNode(_component_v_text_field, {
              class: "rounded",
              type: "text",
              value: formatDateValue(value, 0),
              variant: "outlined",
              "hide-details": "",
              density: "compact",
              onInput: onInput,
              onKeydown: _withKeys(onEnter, ["enter"]),
              onBlur: onBlur
            }, null, 8, ["value", "onInput", "onKeydown", "onBlur"])
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createVNode(_component_fa_icon, {
                class: "mr-2",
                icon: ['far', 'calendar']
              }),
              _createElementVNode("div", _hoisted_7, _toDisplayString(_unref(t)('Search#Form#Field Departure')), 1)
            ]),
            _createVNode(_component_v_text_field, {
              class: "rounded",
              type: "text",
              value: formatDateValue(value, 1),
              "hide-details": "",
              variant: "outlined",
              density: "compact",
              onInput: onInput,
              onKeydown: _withKeys(onEnter, ["enter"]),
              onBlur: onBlur
            }, null, 8, ["value", "onInput", "onKeydown", "onBlur"])
          ])
        ]),
        (_unref(lgAndUp))
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _createElementVNode("p", null, _toDisplayString(_unref(t)('Search#Form#NumNights nights stay', numNights.value)), 1)
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["modelValue", "dark", "locale", "inline", "multi-calendars", "min-date", "range", "markers", "disabled-dates", "disabled-week-days"])
  ], 2))
}
}

})